import { useEffect } from 'react';
import { has } from 'lodash';
import { setFormError } from 'utils/form';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useForm, SubmitHandler, ErrorOption } from 'react-hook-form';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { Button } from 'ui';
import useToastContext from 'ui/hooks/useToast';
import { ToastProps } from 'ui/contexts/overlay/Toast';
import { useLoading } from 'ui/contexts/overlay/Loading';
import { Page } from 'dashboard/components/dashboard/Breadcrumbs';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';
import {
  FinancialAccountCreated,
  FinancialAccountMutationVariables,
  FINANCIAL_ACCOUNT_CREATE_MUTATION,
} from 'financialAccounts/graphql/financialAccountCreateMutation';
import {
  financialAccountsTypename,
  FinancialAccountList,
  FINANCIAL_ACCOUNTS_QUERY,
} from 'financialAccounts/graphql/financialAccountsQuery';
import FinancialAccountFormFields, {
  FormFields,
} from 'financialAccounts/components/form/FinancialAccountsFormFields';
import {
  BankList,
  BANKS_QUERY,
  BANKS_SELECT_QUERY_VARIABLES,
} from 'banks/graphql/banksQuery';
import { unMaskFormFields } from 'utils/unmask';

const DASHBOARD_FINANCIAL_ACCOUNTS_ROUTE =
  '/dashboard/billing-cycle/financial-accounts';

const BREADCRUMB_PAGES: Page[] = [
  {
    name: 'Contas Financeiras',
    route: DASHBOARD_FINANCIAL_ACCOUNTS_ROUTE,
    current: false,
  },
  {
    name: 'Nova Conta Financeira',
    route: null,
    current: true,
  },
];
const TITLE = 'Nova Conta Financeira';

const CREATE_ERROR_TOAST: ToastProps = {
  text: 'Houve um erro ao tentar criar a conta financeira',
  title: 'Algo deu errado!',
  variant: 'danger',
};

const CREATE_SUCCESS_TOAST: ToastProps = {
  title: 'Sucesso!',
  variant: 'primary',
  text: 'Sucesso ao cadastrar a Conta Financeira.',
};

export default function CreateFinancialAccountsPage() {
  const { push } = useHistory();
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const { showLoading, closeLoading, LoadingOverlay } = useLoading();

  const {
    register,
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormFields>();

  const [
    banksSelect,
    { data: dataBanks, loading: loadingBanks, refetch: refetchBanks },
  ] = useLazyQuery<BankList>(BANKS_QUERY, BANKS_SELECT_QUERY_VARIABLES);

  useEffect(() => {
    refetchBanks && refetchBanks();

    banksSelect();
  }, [banksSelect, refetchBanks]);

  const [financialAccountCreateMutation, { loading: loadingCreate }] = useMutation<
    FinancialAccountCreated,
    FinancialAccountMutationVariables
  >(FINANCIAL_ACCOUNT_CREATE_MUTATION, {
    onError(error: ApolloError) {
      if (has(error.graphQLErrors[0], 'message')) {
        addToast(CREATE_ERROR_TOAST);
      }

      setFormError(
        error,
        (field: string, error: ErrorOption) => {
          setError(field as keyof FormFields, error);

          setTimeout(() => clearErrors(), 2500);
        },
        t
      );
    },
    onCompleted() {
      addToast(CREATE_SUCCESS_TOAST);
      push(DASHBOARD_FINANCIAL_ACCOUNTS_ROUTE);
    },
  });

  const onSubmit: SubmitHandler<FormFields> = (financialAccountCreateInput) => {
    financialAccountCreateInput = unMaskFormFields(financialAccountCreateInput);

    financialAccountCreateMutation({
      variables: {
        financialAccountCreateInput: {
          ...financialAccountCreateInput,
          financialAccountAddressPostalCode: Number(
            financialAccountCreateInput.financialAccountAddressPostalCode
          ),
          bankId: Number(financialAccountCreateInput.bankId),
        },
      },
      update(cache, { data }) {
        const existingData = cache.readQuery<FinancialAccountList>({
          query: FINANCIAL_ACCOUNTS_QUERY,
        });
        cache.writeQuery({
          query: FINANCIAL_ACCOUNTS_QUERY,
          data: {
            financialAccounts: {
              __typename: financialAccountsTypename,
              afterCursor: existingData?.financialAccounts.afterCursor || null,
              beforeCursor: existingData?.financialAccounts.beforeCursor || null,
              entries: [
                data?.financialAccountCreate,
                ...(existingData?.financialAccounts.entries || []),
              ],
            },
          },
        });
      },
    });
  };

  const isLoading = loadingCreate || loadingBanks;

  useEffect(() => {
    if (isLoading) {
      showLoading();
      return;
    }

    closeLoading();
  }, [isLoading, showLoading, closeLoading]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingOverlay />
      <Dashboard
        dashboardHeader={<></>}
        dashboardMainHeaderTitle={
          <DashboardMainHeaderForm breadcrumbPages={BREADCRUMB_PAGES} title={TITLE}>
            <Button type="submit" size="sm" disabled={isLoading}>
              Salvar
            </Button>
          </DashboardMainHeaderForm>
        }
      >
        <FinancialAccountFormFields
          errors={errors}
          control={control}
          register={register}
          banks={dataBanks?.banks.entries || []}
        />
      </Dashboard>
    </form>
  );
}
